import { useEffect, useRef, type CSSProperties } from 'react'
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands'
import { markdown, markdownKeymap } from '@codemirror/lang-markdown'
import {
  HighlightStyle,
  syntaxHighlighting,
  syntaxTree,
} from '@codemirror/language'
import { Compartment, EditorSelection, EditorState, StateField } from '@codemirror/state'
import {
  Decoration,
  DecorationSet,
  drawSelection,
  EditorView,
  keymap,
  placeholder,
} from '@codemirror/view'
import { tags as t } from '@lezer/highlight'

const HIDDEN_MARK_TYPES = new Set([
  'HeaderMark',
  'QuoteMark',
  'LinkMark',
  'EmphasisMark',
  'CodeMark',
  'StrikethroughMark',
  'SuperscriptMark',
  'SubscriptMark',
  'URL',
])

function intersectsCursorLine(state: EditorState, from: number, to: number): boolean {
  const head = state.selection.main.head
  const anchor = state.selection.main.anchor
  const curStart = Math.min(head, anchor)
  const curEnd = Math.max(head, anchor)
  const startLine = state.doc.lineAt(from).number
  const endPos = Math.max(from, to - 1)
  const endLine = state.doc.lineAt(endPos).number
  const selStartLine = state.doc.lineAt(curStart).number
  const selEndLine = state.doc.lineAt(curEnd).number
  return selEndLine >= startLine && selStartLine <= endLine
}

const hiddenMark = Decoration.mark({ class: 'cm-md-hidden-mark' })

function buildLivePreviewDecorations(state: EditorState): DecorationSet {
  const ranges: ReturnType<typeof hiddenMark.range>[] = []
  syntaxTree(state).iterate({
    enter: (node) => {
      const name = node.type.name
      if (!HIDDEN_MARK_TYPES.has(name)) return
      if (node.from === node.to) return
      if (intersectsCursorLine(state, node.from, node.to)) return
      ranges.push(hiddenMark.range(node.from, node.to))
    },
  })
  ranges.sort((a, b) => a.from - b.from || a.to - b.to)
  return Decoration.set(ranges)
}

const livePreviewField = StateField.define<DecorationSet>({
  create(state) {
    return buildLivePreviewDecorations(state)
  },
  update(deco, tr) {
    if (!tr.docChanged && !tr.selection) return deco
    return buildLivePreviewDecorations(tr.state)
  },
  provide: (f) => EditorView.decorations.from(f),
})

const markdownHighlightStyle = HighlightStyle.define([
  { tag: t.heading1, class: 'cm-md-h1' },
  { tag: t.heading2, class: 'cm-md-h2' },
  { tag: t.heading3, class: 'cm-md-h3' },
  { tag: t.heading4, class: 'cm-md-h4' },
  { tag: t.heading5, class: 'cm-md-h5' },
  { tag: t.heading6, class: 'cm-md-h6' },
  { tag: t.strong, class: 'cm-md-strong' },
  { tag: t.emphasis, class: 'cm-md-em' },
  { tag: t.strikethrough, class: 'cm-md-strike' },
  { tag: t.monospace, class: 'cm-md-code' },
  { tag: t.link, class: 'cm-md-link' },
  { tag: t.url, class: 'cm-md-url' },
  { tag: t.quote, class: 'cm-md-quote' },
  { tag: t.list, class: 'cm-md-list' },
])

const markdownEditorTheme = EditorView.theme({
  '&': {
    fontSize: 'inherit',
    backgroundColor: 'transparent',
    height: '100%',
  },
  '.cm-scroller': {
    fontFamily: 'var(--font-serif)',
    lineHeight: '1.7',
    overflow: 'auto',
    height: '100%',
  },
  '.cm-content': {
    padding: '4px 0',
    minHeight: 'inherit',
  },
  '.cm-line': {
    padding: '0',
  },
  '&.cm-focused': {
    outline: 'none',
  },
  '.cm-placeholder': {
    color: 'var(--color-ink-muted)',
    fontStyle: 'italic',
  },
})

export interface MarkdownEditorProps {
  value: string
  onChange: (next: string) => void
  placeholder?: string
  readOnly?: boolean
  autoFocus?: boolean
  minHeight?: string
  className?: string
  style?: CSSProperties
}

export function MarkdownEditor({
  value,
  onChange,
  placeholder: placeholderText,
  readOnly = false,
  autoFocus = false,
  minHeight = '200px',
  className,
  style,
}: MarkdownEditorProps) {
  const hostRef = useRef<HTMLDivElement>(null)
  const viewRef = useRef<EditorView | null>(null)
  const onChangeRef = useRef(onChange)
  const readOnlyRef = useRef(readOnly)
  const lastEmittedRef = useRef<string>(value)
  onChangeRef.current = onChange
  readOnlyRef.current = readOnly

  const readOnlyCompartmentRef = useRef(new Compartment())
  const placeholderCompartmentRef = useRef(new Compartment())
  const isProgrammaticRef = useRef(false)

  useEffect(() => {
    const parent = hostRef.current
    if (!parent) return

    const placeholderExt = placeholderText ? placeholder(placeholderText) : []

    const extensions = [
      markdown({ codeLanguages: [] }),
      history(),
      drawSelection(),
      syntaxHighlighting(markdownHighlightStyle),
      livePreviewField,
      markdownEditorTheme,
      EditorView.lineWrapping,
      keymap.of([
        ...markdownKeymap,
        ...defaultKeymap,
        ...historyKeymap,
        indentWithTab,
      ]),
      readOnlyCompartmentRef.current.of(
        EditorState.readOnly.of(readOnlyRef.current)
      ),
      placeholderCompartmentRef.current.of(placeholderExt),
      EditorView.updateListener.of((update) => {
        if (update.docChanged && !isProgrammaticRef.current) {
          const next = update.state.doc.toString()
          lastEmittedRef.current = next
          onChangeRef.current(next)
        }
      }),
      EditorView.contentAttributes.of({ class: 'markdown-content' }),
    ]

    const state = EditorState.create({
      doc: value,
      selection: EditorSelection.cursor(value.length),
      extensions,
    })

    const view = new EditorView({
      state,
      parent,
    })
    viewRef.current = view
    lastEmittedRef.current = value

    requestAnimationFrame(() => {
      if (viewRef.current !== view) return
      view.requestMeasure()
      if (autoFocus) view.focus()
    })

    return () => {
      view.destroy()
      viewRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- editor mount once; sync doc via separate effect
  }, [])

  useEffect(() => {
    const view = viewRef.current
    if (!view) return
    view.dispatch({
      effects: readOnlyCompartmentRef.current.reconfigure(
        EditorState.readOnly.of(readOnly)
      ),
    })
  }, [readOnly])

  useEffect(() => {
    const view = viewRef.current
    if (!view) return
    view.dispatch({
      effects: placeholderCompartmentRef.current.reconfigure(
        placeholderText ? placeholder(placeholderText) : []
      ),
    })
  }, [placeholderText])

  useEffect(() => {
    const view = viewRef.current
    if (!view) return
    const cur = view.state.doc.toString()
    if (cur === value) {
      lastEmittedRef.current = value
      return
    }
    if (value === lastEmittedRef.current) return
    const prevHead = view.state.selection.main.head
    /** Parent often bumps value after mount (e.g. draft loads); empty→filled should land at EOF. */
    const head =
      cur.length === 0 && value.length > 0 ? value.length : Math.min(prevHead, value.length)
    try {
      isProgrammaticRef.current = true
      view.dispatch({
        changes: { from: 0, to: cur.length, insert: value },
        selection: EditorSelection.cursor(head),
      })
    } finally {
      isProgrammaticRef.current = false
    }
    lastEmittedRef.current = value
  }, [value])

  return (
    <div
      ref={hostRef}
      className={className}
      style={{
        minHeight,
        ...style,
      }}
    />
  )
}
